import { useRef, useEffect } from 'react';

export type UsePopoverTimeoutsReturn = {
  scheduleOpen: (callback: () => void) => void;
  scheduleClose: (callback: () => void) => void;
  scheduleDisplayBlock: (callback: () => void) => void;
  scheduleAutoFocus: (callback: () => void, delay?: number) => void;
  clearAllTimeouts: () => void;
};

/**
 * Manages all animation and timing-related timeouts for the Popover component.
 * Provides unified interface for scheduling callbacks and ensures proper cleanup.
 */
export function usePopoverTimeouts(): UsePopoverTimeoutsReturn {
  const closeTimeout = useRef<NodeJS.Timeout | null>(null);
  const renderTimer = useRef<NodeJS.Timeout | null>(null);
  const openAutoFocusTimer = useRef<NodeJS.Timeout | null>(null);
  const setDisplayBlockTimer = useRef<NodeJS.Timeout | null>(null);

  const clearAllTimeouts = () => {
    if (closeTimeout.current) {
      clearTimeout(closeTimeout.current);
      closeTimeout.current = null;
    }
    if (renderTimer.current) {
      clearTimeout(renderTimer.current);
      renderTimer.current = null;
    }
    if (openAutoFocusTimer.current) {
      clearTimeout(openAutoFocusTimer.current);
      openAutoFocusTimer.current = null;
    }
    if (setDisplayBlockTimer.current) {
      clearTimeout(setDisplayBlockTimer.current);
      setDisplayBlockTimer.current = null;
    }
  };

  const scheduleOpen = (callback: () => void) => {
    if (renderTimer.current) {
      clearTimeout(renderTimer.current);
    }
    renderTimer.current = setTimeout(callback, 150);
  };

  const scheduleClose = (callback: () => void) => {
    if (closeTimeout.current) {
      clearTimeout(closeTimeout.current);
    }
    closeTimeout.current = setTimeout(callback, 150);
  };

  const scheduleDisplayBlock = (callback: () => void) => {
    if (setDisplayBlockTimer.current) {
      clearTimeout(setDisplayBlockTimer.current);
    }
    setDisplayBlockTimer.current = setTimeout(callback, 0);
  };

  const scheduleAutoFocus = (callback: () => void, delay = 150) => {
    if (openAutoFocusTimer.current) {
      clearTimeout(openAutoFocusTimer.current);
    }
    openAutoFocusTimer.current = setTimeout(callback, delay);
  };

  // Cleanup all timeouts on unmount
  useEffect(() => {
    return () => {
      clearAllTimeouts();
    };
  }, []);

  return {
    scheduleOpen,
    scheduleClose,
    scheduleDisplayBlock,
    scheduleAutoFocus,
    clearAllTimeouts,
  };
}
